import { Stack, Tab, Tabs, Typography } from '@mui/material';
import {
  Provider,
  type Account,
  type CloudAccountConfiguration,
} from '@repo/api-gw-sdk';
import { useState } from 'react';

import { CircleImage } from '@/components/shared/circleImage';
import { Wizard } from '@/components/wizard/Wizard';
import { CloudProviders } from '@/data/inventory/data';

import { RestoreAccountConfigCustomTags } from './restoreAccountConfigCustomTags';
import {
  RestoreAccountConfigFlow,
  type RestoreAccountConfigState,
} from './restoreAccountConfigFlow';

export const RestoreAccountConfigTabs = (props: {
  account: Account;
  config: CloudAccountConfiguration;
  onSave: (
    account: Account,
    entity: CloudAccountConfiguration
  ) => Promise<void>;
  onAbort: () => void;
}) => {
  const [selectedTab, setSelectedTab] = useState(
    props.account.cloudProvider === Provider.Aws ? 'Connectivity' : 'Tags'
  );
  const save = async (state: RestoreAccountConfigState) => {
    await props.onSave(state.account, state.config);
    return true;
  };

  return (
    <>
      <Stack gap='12px' className='px-[50px]'>
        <Stack direction='row' alignItems='center'>
          {CloudProviders[props.account.cloudProvider] && (
            <CircleImage
              key={props.account.cloudProvider}
              alt={CloudProviders[props.account.cloudProvider]?.displayName}
              src={CloudProviders[props.account.cloudProvider]?.logo}
              className='mr-[8px]'
            />
          )}
          {props.account.providerAccountId}
        </Stack>
        <Typography className='max-w-[680px]' variant='body1'>
          {`Restore operations are run on a temporary server. If the server’s default settings don’t meet your requirements, you can customize them here.`}
        </Typography>
      </Stack>
      <Tabs value={selectedTab} className='my-[24px] pl-[40px]'>
        {props.account.cloudProvider === Provider.Aws && (
          <Tab
            value={'Connectivity'}
            key={0}
            label={<Typography variant='subtitle1'>Connectivity</Typography>}
            onClick={() => setSelectedTab('Connectivity')}
          />
        )}
        <Tab
          key={1}
          value={'Tags'}
          label={<Typography variant='subtitle1'>Tags</Typography>}
          onClick={() => setSelectedTab('Tags')}
        />
      </Tabs>
      {selectedTab === 'Connectivity' && (
        <Wizard<RestoreAccountConfigState>
          hideBackArrow={true}
          onAbort={props.onAbort}
          onFinish={save}
          flow={[RestoreAccountConfigFlow]}
          initialState={{
            account: props.account,
            config: { ...props.config },
          }}
        />
      )}
      {selectedTab === 'Tags' && (
        <RestoreAccountConfigCustomTags
          account={props.account}
          close={props.onAbort}
        />
      )}
    </>
  );
};
